const VIETNAM_TIME_ZONE = "Asia/Ho_Chi_Minh";
const VIETNAM_UTC_OFFSET_MS = 7 * 60 * 60 * 1000;

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function getVietnamDateTimeParts(date: Date): DateTimeParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: VIETNAM_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);

  return {
    year: getPart("year"),
    month: getPart("month"),
    day: getPart("day"),
    hour: getPart("hour"),
    minute: getPart("minute"),
  };
}

function toDateTimeLocalValue({
  year,
  month,
  day,
  hour,
  minute,
}: DateTimeParts) {
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
}

/** Formats an instant as a datetime-local value in Vietnam (UTC+7). */
export function toVietnamDateTimeLocalValue(date: Date) {
  return toDateTimeLocalValue(getVietnamDateTimeParts(date));
}

/** Returns the start of today in Vietnam, regardless of the browser timezone. */
export function getVietnamStartOfTodayValue(now = new Date()) {
  const vietnamNow = getVietnamDateTimeParts(now);
  return toDateTimeLocalValue({ ...vietnamNow, hour: 0, minute: 0 });
}

/** Returns a Vietnam datetime-local value for the beginning of the month. */
export function getVietnamStartOfMonthValue(now = new Date()) {
  const vietnamNow = getVietnamDateTimeParts(now);
  return toDateTimeLocalValue({
    ...vietnamNow,
    day: 1,
    hour: 0,
    minute: 0,
  });
}

/** Returns the start of the Vietnam calendar day a number of days before now. */
export function getVietnamStartOfDayDaysAgoValue(daysAgo: number, now = new Date()) {
  const vietnamNow = getVietnamDateTimeParts(now);
  const date = new Date(
    Date.UTC(vietnamNow.year, vietnamNow.month - 1, vietnamNow.day - daysAgo),
  );

  return toDateTimeLocalValue({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: 0,
    minute: 0,
  });
}

/**
 * Interprets a datetime-local value as Vietnam time and serializes it as UTC
 * for API payloads. This avoids using the browser's local timezone.
 */
export function vietnamDateTimeLocalToIso(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return "";

  const [, year, month, day, hour, minute] = match;
  const vietnamLocalAsUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );
  const date = new Date(vietnamLocalAsUtc);

  const isValid =
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day) &&
    date.getUTCHours() === Number(hour) &&
    date.getUTCMinutes() === Number(minute);

  return isValid
    ? new Date(vietnamLocalAsUtc - VIETNAM_UTC_OFFSET_MS).toISOString()
    : "";
}
